import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { IApiQRCode, IQRCodeFormData, QRCodeInputMode } from "../../types";
import { qrcodeValidationSchema } from "../../validations/validations";
import { INITIAL_QRCODE_SETTINGS } from "../configs";
import { QR_IMAGE_MAX_SIZE, QR_IMAGE_MIN_SIZE } from "../constants";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";
import useUploadAssets from "./useUploadAssets";

interface UseQRCodeSettingsProps {
    characterId: string;
}

export const useQRCodeSettings = ({ characterId }: UseQRCodeSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });
    const { handleUploadFile, isUploading } = useUploadAssets();
    const [dimensionError, setDimensionError] = useState<string | null>(null);
    const hasInitialized = useRef(false); // 追蹤Race Condition

    // ======================= 驗證 Schema =======================
    const validationSchema = useMemo(() => qrcodeValidationSchema(t), [t]);

    // ======================= 表單設置 =======================
    const methods = useForm<IQRCodeFormData>({
        defaultValues: {
            QRcode_show_mode: INITIAL_QRCODE_SETTINGS.QRcode_show_mode,
            inputMode: INITIAL_QRCODE_SETTINGS.inputMode,
            url: INITIAL_QRCODE_SETTINGS.QRcode_Url,
            imageFile: null,
            imageUrl: "",
        },
        resolver: yupResolver(validationSchema),
        mode: "onChange",
    });

    const { control, handleSubmit, setValue, formState, reset } = methods;

    const watchedEnabled = useWatch({ control, name: "QRcode_show_mode" });
    const watchedInputMode = useWatch({ control, name: "inputMode" });

    // ======================= Toggle Handler =======================
    const isEnabled = watchedEnabled;
    const handleToggle = useCallback(
        (enabled: boolean) => {
            setValue("QRcode_show_mode", enabled, { shouldDirty: true });
        },
        [setValue]
    );
    const watchedImageFile = useWatch({ control, name: "imageFile" });
    const watchedImageUrl = useWatch({ control, name: "imageUrl" });
    const watchedUrl = useWatch({ control, name: "url" });

    // ======================= 圖片尺寸驗證 =======================
    const validateImageDimensions = useCallback(
        (file: File): Promise<{ isValid: boolean; width: number; height: number; error?: string }> => {
            return new Promise((resolve) => {
                const img = new Image();
                const objectUrl = URL.createObjectURL(file);

                img.onload = () => {
                    URL.revokeObjectURL(objectUrl);
                    const { width, height } = img;

                    if (width < QR_IMAGE_MIN_SIZE || height < QR_IMAGE_MIN_SIZE) {
                        resolve({
                            isValid: false,
                            width,
                            height,
                            error:
                                t("APPWIDGET.QRCODE_IMAGE_TOO_SMALL", { min: QR_IMAGE_MIN_SIZE }) ||
                                `Image too small (min: ${QR_IMAGE_MIN_SIZE}px)`,
                        });
                    } else if (width > QR_IMAGE_MAX_SIZE || height > QR_IMAGE_MAX_SIZE) {
                        resolve({
                            isValid: false,
                            width,
                            height,
                            error:
                                t("APPWIDGET.QRCODE_IMAGE_TOO_LARGE", { max: QR_IMAGE_MAX_SIZE }) ||
                                `Image too large (max: ${QR_IMAGE_MAX_SIZE}px)`,
                        });
                    } else {
                        resolve({
                            isValid: true,
                            width,
                            height,
                        });
                    }
                };

                img.onerror = () => {
                    URL.revokeObjectURL(objectUrl);
                    resolve({
                        isValid: false,
                        width: 0,
                        height: 0,
                        error: t("APPWIDGET.QRCODE_IMAGE_INVALID") || "Invalid image file",
                    });
                };

                img.src = objectUrl;
            });
        },
        [t]
    );

    // ======================= 圖片上傳處理器 =======================
    const handleImageUpload = useCallback(
        async (file: File): Promise<string | null> => {
            setDimensionError(null);

            const validation = await validateImageDimensions(file);

            if (!validation.isValid) {
                setDimensionError(validation.error || t("APPWIDGET.QRCODE_IMAGE_INVALID"));
                customToast.error(validation.error || t("APPWIDGET.QRCODE_IMAGE_INVALID"));
                return null;
            }
            const response = await handleUploadFile({
                file,
                prefix: "image",
            });

            if (!response) {
                customToast.error(t("COMMON.UPLOAD_FAILED") || "上傳失敗");
                return null;
            }
            return response.url;
        },
        [handleUploadFile, validateImageDimensions, t]
    );

    // ======================= 資料轉換 =======================
    const transformAPItoForm = useCallback((apiData: IApiQRCode | undefined): IQRCodeFormData => {
        if (!apiData) {
            return {
                QRcode_show_mode: INITIAL_QRCODE_SETTINGS.QRcode_show_mode,
                inputMode: INITIAL_QRCODE_SETTINGS.inputMode || "url",
                url: INITIAL_QRCODE_SETTINGS.QRcode_Url,
                imageFile: null,
                imageUrl: "",
            };
        }

        let detectedMode: "url" | "image";

        if (apiData.inputMode) {
            // 使用明確的 inputMode (優先)
            detectedMode = apiData.inputMode;
        } else {
            detectedMode = "url";
        }

        return {
            QRcode_show_mode: apiData.QRcode_show_mode,
            inputMode: detectedMode,
            url: detectedMode === "url" ? apiData.QRcode_Url || "" : "",
            imageFile: null,
            imageUrl: detectedMode === "image" ? apiData.QRcode_Url || "" : "",
        };
    }, []);

    const transformFormToAPI = useCallback((formData: IQRCodeFormData): IApiQRCode => {
        const finalUrl = formData.inputMode === "url" ? formData.url : formData.imageUrl;

        return {
            QRcode_show_mode: formData.QRcode_show_mode,
            QRcode_Url: finalUrl || "",
            inputMode: formData.inputMode,
        };
    }, []);

    // ======================= 表單提交處理 =======================
    const onSubmit = useCallback(
        async (data: IQRCodeFormData) => {
            try {
                if (data.QRcode_show_mode && data.inputMode === "image" && data.imageFile) {
                    const uploadedUrl = await handleImageUpload(data.imageFile);
                    if (!uploadedUrl) {
                        return;
                    }
                    setValue("imageUrl", uploadedUrl);
                    setValue("imageFile", null);
                    data.imageUrl = uploadedUrl;
                    data.imageFile = null;
                }

                const apiPayload = transformFormToAPI(data);
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, { QRcode: apiPayload });

                await updateSettings({ native: mergedNative });

                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            }
        },
        [handleImageUpload, transformFormToAPI, kioskSetting, updateSettings, setValue, reset, t]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("qrcode", formState.isDirty, saveHandler);

    // ======================= 初始化表單從 API =======================
    useEffect(() => {
        // 只在初始載入時重置表單，避免在保存後重置表單，這樣可以避免在 kioskSetting 更改後重置表單，避免競態條件
        if (!isLoadingKioskSetting && !hasInitialized.current) {
            const qrcodeData = kioskSetting?.data?.native?.QRcode;
            const formData = transformAPItoForm(qrcodeData);
            reset(formData);
            hasInitialized.current = true;
        }
    }, [isLoadingKioskSetting, kioskSetting?.data?.native?.QRcode, reset, transformAPItoForm]);

    // 重置初始化標誌當 characterId 更改時（切換到不同的角色）
    useEffect(() => {
        hasInitialized.current = false;
    }, [characterId]);

    // ======================= 模式切換處理 =======================
    const handleModeChange = useCallback(
        (newMode: QRCodeInputMode) => {
            setValue("inputMode", newMode, { shouldDirty: true });
            setDimensionError(null);

            if (newMode === "url") {
                setValue("imageFile", null);
                setValue("imageUrl", "");
            } else {
                setValue("url", "");
            }
        },
        [setValue]
    );

    // ======================= 文件更改處理 =======================
    const handleFileChange = useCallback(
        async (file: File | null): Promise<boolean> => {
            setDimensionError(null);

            if (!file) {
                setValue("imageFile", null);
                return false;
            }

            const validation = await validateImageDimensions(file);

            if (!validation.isValid) {
                setDimensionError(validation.error || t("APPWIDGET.QRCODE_IMAGE_INVALID"));
                customToast.error(validation.error || t("APPWIDGET.QRCODE_IMAGE_INVALID"));
                setValue("imageFile", null);
                return false;
            }

            setValue("imageFile", file, { shouldDirty: true });
            setValue("imageUrl", "");
            return true;
        },
        [setValue, validateImageDimensions, t]
    );

    // ======================= 計算值 =======================
    const hasValidInput = useMemo(() => {
        if (!watchedEnabled) return true;

        if (watchedInputMode === "url") {
            return Boolean(watchedUrl && watchedUrl.trim() !== "");
        } else {
            return Boolean(watchedImageFile) || Boolean(watchedImageUrl && watchedImageUrl.trim() !== "");
        }
    }, [watchedEnabled, watchedInputMode, watchedUrl, watchedImageFile, watchedImageUrl]);

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        formState,

        // Toggle state
        isEnabled,
        handleToggle,

        // State
        watchedEnabled,
        watchedInputMode,
        watchedImageFile,
        watchedImageUrl,
        watchedUrl,
        dimensionError,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isUploading,

        // Handlers
        handleModeChange,
        handleFileChange,
        hasValidInput,

        // Constants
        QR_IMAGE_MIN_SIZE,
        QR_IMAGE_MAX_SIZE,
    };
};

export default useQRCodeSettings;
